import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Nav, NavItem, NavLink, Badge, Spinner } from 'reactstrap';
import { H5, H6 } from '../../../AbstractElements';
import ReactApexChart from 'react-apexcharts';
import { TrendingUp, Users, UserCheck, UserX, Mail } from 'react-feather';

const UserRVOPVChart = ({ usersRVOPV = [], loading }) => {
  const [activeTab, setActiveTab] = useState('daily');
  const [chartData, setChartData] = useState({
    series: [{ name: 'Users', data: [] }],
    options: {
      chart: { type: 'line', height: 350, toolbar: { show: false } },
      stroke: { curve: 'smooth', width: 3 },
      colors: ['#f73164'],
      xaxis: { categories: [] },
      yaxis: { title: { text: 'Số lượng User' } },
      grid: { borderColor: '#f1f1f1' },
      dataLabels: { enabled: false },
      markers: { size: 5, hover: { size: 8 } }
    }
  });

  // Tính toán thống kê từ dữ liệu thực
  const getStats = () => {
    if (!usersRVOPV || usersRVOPV.length === 0) {
      return {
        totalUsers: 0,
        activeUsers: 0,
        inactiveUsers: 0,
        withEmail: 0
      };
    }

    const totalUsers = usersRVOPV.length;
    const activeUsers = usersRVOPV.filter(user => user.isActive !== false).length;
    const inactiveUsers = totalUsers - activeUsers;
    const withEmail = usersRVOPV.filter(user => user.email && user.email !== '').length;

    return { totalUsers, activeUsers, inactiveUsers, withEmail };
  };

  // Tạo dữ liệu biểu đồ dựa trên tab
  const generateChartData = () => {
    const stats = getStats();
    let categories = [];
    let data = [];

    switch (activeTab) {
      case 'daily':
        categories = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        data = [70, 85, 75, 90, 82, 88, 95].map(val => Math.floor(val * stats.totalUsers / 100));
        break;
      case 'weekly':
        categories = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
        data = [stats.totalUsers * 0.7, stats.totalUsers * 0.8, stats.totalUsers * 0.9, stats.totalUsers];
        break;
      case 'monthly':
        categories = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
        data = [stats.totalUsers * 0.5, stats.totalUsers * 0.6, stats.totalUsers * 0.7, stats.totalUsers * 0.8, stats.totalUsers * 0.9, stats.totalUsers];
        break;
      default:
        categories = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        data = [70, 85, 75, 90, 82, 88, 95];
    }

    setChartData({
      series: [{ name: 'Users RVOPV', data }],
      options: {
        ...chartData.options,
        xaxis: { ...chartData.options.xaxis, categories },
        title: { text: `User Growth Statistics - ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}` }
      }
    });
  };

  useEffect(() => {
    generateChartData();
  }, [activeTab, usersRVOPV]);

  const stats = getStats();
  const statsData = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      color: 'primary',
      icon: Users,
      description: 'Tổng số user'
    },
    {
      title: 'Active Users',
      value: stats.activeUsers,
      color: 'success',
      icon: UserCheck,
      description: 'User đang hoạt động'
    },
    {
      title: 'Inactive Users',
      value: stats.inactiveUsers,
      color: 'danger',
      icon: UserX,
      description: 'User không hoạt động'
    },
    {
      title: 'With Email',
      value: stats.withEmail,
      color: 'info',
      icon: Mail,
      description: 'User có email'
    }
  ];

  if (loading) {
    return (
      <Col xxl="12" className="box-col-12">
        <Card>
          <CardBody className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="text-center">
              <Spinner color="primary" />
              <p className="mt-2 mb-0">Đang tải biểu đồ User RVOPV...</p>
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  }

  return (
    <Col xxl="12" className="box-col-12">
      <Card>
        <CardHeader className="card-no-border">
          <H5>User Growth Statistics</H5>
          <Nav className="nav-pill-border">
            {['daily', 'weekly', 'monthly'].map((tab) => (
              <NavItem key={tab}>
                <NavLink
                  className={`cursor-pointer ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </CardHeader>
        <CardBody>
          <Row className="g-3 mb-4">
            {statsData.map((item, index) => {
              const Icon = item.icon;
              return (
                <Col key={index} xl="3" md="6" sm="6" className="box-col-6">
                  <Card className="stat-widget">
                    <CardBody>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex flex-column justify-content-center" style={{ minWidth: 0, flex: 1 }}>
                          <span className="f-light f-w-500 f-14 d-block mb-2">{item.title}</span>
                          <H6 attrH6={{ className: 'mb-0' }}>
                            <Badge color={item.color} className="badge-lg">
                              {item.value}
                            </Badge>
                          </H6>
                          <small className="text-muted mt-1">{item.description}</small>
                        </div>
                        <div 
                          className={`bg-${item.color} rounded d-flex align-items-center justify-content-center`} 
                          style={{ width: '48px', height: '48px', flexShrink: 0 }}
                        >
                          <Icon size={22} className="text-white" />
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>

          <div className="chart-container">
            <ReactApexChart
              options={chartData.options}
              series={chartData.series}
              type="line"
              height={350}
            />
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

export default UserRVOPVChart;

